/**
 * Amend Instances Tab
 *
 * Tab component for managing DICOM instances (images) in the amend flow
 */

import { useState, useCallback } from 'react'
import { Upload, Trash2, Image as ImageIcon, RefreshCcw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { AuthenticatedImage } from '@/presentation/components/ui/AuthenticatedImage'
import { cn } from '@/lib/utils'
import { tokenStorage } from '@/lib/api'
import type { AmendStudyData, AmendInstanceInfo } from '@/domain/study/types'

interface AmendInstancesTabProps {
  studyId: string
  instances: AmendInstanceInfo[]
  deleteInstanceIds: string[]
  onDeleteChange: (ids: string[]) => void
  newInstances: NonNullable<AmendStudyData['newInstances']>
  onNewInstancesChange: (instances: AmendStudyData['newInstances']) => void
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      resolve(result.split(',')[1])
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function AmendInstancesTab({
  instances,
  deleteInstanceIds,
  onDeleteChange,
  newInstances,
  onNewInstancesChange,
}: AmendInstancesTabProps) {
  const [isDragOver, setIsDragOver] = useState(false)

  const toggleDelete = (id: string) => {
    if (deleteInstanceIds.includes(id)) {
      onDeleteChange(deleteInstanceIds.filter((i) => i !== id))
    } else {
      onDeleteChange([...deleteInstanceIds, id])
    }
  }

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const dicomFiles = fileArray.filter(
        (f) => f.name.endsWith('.dcm') || f.type === 'application/dicom'
      )

      const newItems: NonNullable<AmendStudyData['newInstances']> = []

      for (const file of dicomFiles) {
        const base64 = await fileToBase64(file)
        newItems.push({
          fileData: base64,
          fileName: file.name,
          sopInstanceUID: `1.2.3.${Date.now()}.${Math.random()}`,
        })
      }

      onNewInstancesChange([...newInstances, ...newItems])
    },
    [newInstances, onNewInstancesChange]
  )

  const removeNewInstance = (index: number) => {
    const updated = [...newInstances]
    updated.splice(index, 1)
    onNewInstancesChange(updated)
  }

  return (
    <div className="space-y-6">
      {/* Current Instances */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Imagenes Actuales ({instances.length})</span>
            {deleteInstanceIds.length > 0 && (
              <span className="text-sm font-normal text-red-600 dark:text-red-400">
                {deleteInstanceIds.length} marcada(s) para eliminar
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {instances.length === 0 ? (
            <p className="text-[var(--foreground-muted)] text-center py-8">
              No hay imagenes en este estudio
            </p>
          ) : (
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
              {instances.map((instance) => {
                const isMarkedForDelete = deleteInstanceIds.includes(instance.id)
                return (
                  <div
                    key={instance.id}
                    className={cn(
                      'relative aspect-square rounded-lg border-2 cursor-pointer transition-all overflow-hidden',
                      isMarkedForDelete
                        ? 'border-red-500 opacity-50'
                        : 'border-[var(--border)] hover:border-[var(--primary)]'
                    )}
                    onClick={() => toggleDelete(instance.id)}
                  >
                    {instance.thumbnailUrl ? (
                      <AuthenticatedImage
                        src={instance.thumbnailUrl}
                        alt={`Instance ${instance.instanceNumber}`}
                        className="w-full h-full object-cover"
                        getToken={() => tokenStorage.getAccessToken()}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[var(--secondary)]">
                        <ImageIcon className="h-6 w-6 text-[var(--foreground-muted)]" />
                      </div>
                    )}
                    <div className="absolute top-1 left-1">
                      <input
                        type="checkbox"
                        checked={isMarkedForDelete}
                        onChange={() => toggleDelete(instance.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </div>
                    {isMarkedForDelete && (
                      <div className="absolute inset-0 flex items-center justify-center bg-red-500/20">
                        <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                      #{instance.instanceNumber || '?'}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {deleteInstanceIds.length > 0 && (
            <div className="mt-4 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDeleteChange([])}
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                Desmarcar todos
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add New Instances */}
      <Card>
        <CardHeader>
          <CardTitle>Agregar Nuevas Imagenes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Drop Zone */}
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              isDragOver
                ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                : 'border-[var(--border)] hover:border-[var(--primary)]/50'
            )}
            onDrop={(e) => {
              e.preventDefault()
              setIsDragOver(false)
              if (e.dataTransfer.files) handleFiles(e.dataTransfer.files)
            }}
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragOver(true)
            }}
            onDragLeave={() => setIsDragOver(false)}
            onClick={() => document.getElementById('dicom-input')?.click()}
          >
            <Upload className="h-8 w-8 mx-auto mb-2 text-[var(--foreground-muted)]" />
            <p className="text-[var(--foreground)]">
              Arrastre archivos DICOM aqui o haga clic para seleccionar
            </p>
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              Solo archivos .dcm
            </p>
            <input
              id="dicom-input"
              type="file"
              multiple
              accept=".dcm,application/dicom"
              className="hidden"
              onChange={(e) => e.target.files && handleFiles(e.target.files)}
            />
          </div>

          {/* New Instances List */}
          {newInstances.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm">
                Archivos pendientes ({newInstances.length})
              </h4>
              {newInstances.map((instance, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded bg-[var(--secondary)]"
                >
                  <div className="flex items-center gap-2">
                    <ImageIcon className="h-4 w-4 text-[var(--foreground-muted)]" />
                    <span className="text-sm">{instance.fileName}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeNewInstance(index)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
